import type { DocumentWithLanguage, FirstClassDocument, WordBoundary } from '../../types';
import { WebviewDocumentChunk } from '../../types/chunkedDocuments';
import type {
  PlaybackRate,
  TextToSpeechGender,
  TextToSpeechInfo,
  TextToSpeechSettings,
  TextToSpeechTrack,
  TTSLanguage,
} from '../../types/tts';
import {
  PlaybackRates,
  TextToSpeechGenderByVoice,
  TextToSpeechVoice,
  TextToSpeechVoicesByLanguage,
  TextToSpeechVoiceToApiVersion,
  TrackPlayerState,
} from '../../types/tts';
import delay from '../../utils/delay';
import { isBookwise } from '../../utils/env.platform';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../../utils/exceptionHandler.platform';
import getDocumentLanguage from '../../utils/getDocumentLanguage';
import getDocumentOverrideOrReal from '../../utils/getDocumentOverrideOrReal';
import getServerBaseUrl from '../../utils/getServerBaseUrl.platform';
// eslint-disable-next-line import/no-cycle
import requestWithAuth from '../../utils/requestWithAuth.platformIncludingExtension';
import database from '../database';
import { CancelStateUpdate, globalState, updateState } from '../models';
// eslint-disable-next-line import/no-cycle
import background from '../portalGates/toBackground';
import { overrideLanguage } from '../stateUpdaters/persistentStateUpdaters/documents/overrides';
import { createToast } from '../toasts.platform';
import type { StateUpdateOptions, StateUpdateOptionsWithoutEventName } from '../types';
import { getAllWebviewChunksForDocId } from '../utils/getAllWebviewChunksForDocId';
import { getSpineForDoc } from '../utils/getSpineForDoc';
import {
  textToSpeechDefaultGender,
  textToSpeechDefaultLanguage,
  textToSpeechDefaultPlaybackRate,
  textToSpeechDefaultVolume,
} from '../utils/tts';

// How far a reported position may sit from a seek target and still count as "at" it.
// Used both to verify seeks and to decide whether a seek lands inside the silence-filled
// region before a mid-chunk start — keep the two in sync.
const SEEK_POSITION_TOLERANCE_SECONDS = 2;

/*
  Public functions are idempotent e.g. calling `.stop()` when the track is already stopped is a no-op.
  "track player" = the underlying API/element/thing that actually plays the audio.
*/
abstract class AbstractTtsController<TTrack extends TextToSpeechTrack = TextToSpeechTrack> {
  trackPlayerCreationPromise = Promise.resolve();
  _startingToPlay = false;
  _pausedSincePlaybackStart = false;
  _playbackEngageGeneration = 0;

  async decreasePlaybackRatePreference(options: StateUpdateOptionsWithoutEventName): Promise<void> {
    const oldRate = this._getSettings()?.playbackRate;
    const possibleNewRate =
      typeof oldRate === 'undefined'
        ? textToSpeechDefaultPlaybackRate
        : Array.from(PlaybackRates)
            .reverse()
            .find((rate) => rate < oldRate);
    if (typeof possibleNewRate !== 'number') {
      return;
    }
    return this.setPlaybackRatePreference(possibleNewRate, {
      ...options,
      eventName: 'settings-tts-playback-rate-decreased',
    });
  }

  async fetchTimestampForElement(docId: string, chunkId: string, elementIndex: number): Promise<number | undefined> {
    const doc = await database.collections.documents.findOne<FirstClassDocument>(docId);
    if (!doc) {
      throw new Error(`_buildTrack: can't find document with id ${docId}`);
    }

    await updateState(
      (state) => {
        if (state.ttsFetchingTimestamp) {
          throw new CancelStateUpdate();
        }
        state.ttsFetchingTimestamp = true;
      },
      { eventName: 'tts-timestamp-fetch-started', userInteraction: null, shouldCreateUserEvent: false },
    );

    const voice = this.getVoiceForDocument(doc);
    const versionEndpoint = TextToSpeechVoiceToApiVersion[voice];
    if (versionEndpoint !== 'v3') {
      return;
    }
    const url = `${getServerBaseUrl()}/reader/api/documents/${docId}/tts_stream_${versionEndpoint}_element_to_timestamp?voice=${voice}&elementIndex=${elementIndex}&chunkId=${chunkId}`;
    const resp = await requestWithAuth(url, {
      credentials: 'include',
      method: 'GET',
      mode: 'cors',
    });
    // After 10 seconds, remove the loading state in case it didnt get removed otherwise
    setTimeout(() => {
      updateState(
        (state) => {
          if (!state.ttsFetchingTimestamp) {
            throw new CancelStateUpdate();
          }
          state.ttsFetchingTimestamp = false;
        },
        { eventName: 'tts-timestamp-fetch-ended', userInteraction: null, shouldCreateUserEvent: false },
      );
    }, 10000);

    const text = await resp.json();
    return text.start_timestamp;
  }

  async getCurrentTTSLanguageForDocId(docId: string | null): Promise<TTSLanguage | undefined> {
    if (!docId) {
      return;
    }

    const doc = await database.collections.documents.findOne<FirstClassDocument>(docId);
    if (!doc) {
      return;
    }

    return this.getCurrentTTSLanguageForDoc(doc);
  }

  getCurrentTTSLanguageForDoc(doc: FirstClassDocument | null): TTSLanguage {
    if (!doc) {
      return textToSpeechDefaultLanguage;
    }

    const language = this._convertLanguageToTTSLanguage(
      getDocumentLanguage(doc) || textToSpeechDefaultLanguage,
    );

    if (!language || !TextToSpeechVoicesByLanguage[language]) {
      return textToSpeechDefaultLanguage;
    }
    return language;
  }

  getVoiceForDocument(doc: FirstClassDocument | null): TextToSpeechVoice {
    const ttsSettings = this._getSettings();
    if (!ttsSettings || !ttsSettings.defaultVoiceSettings || !doc) {
      return this._getDefaultVoiceForLanguageAndGender(
        textToSpeechDefaultLanguage,
        textToSpeechDefaultGender,
      );
    }
    const currentTTSLanguageForDoc = this.getCurrentTTSLanguageForDoc(doc);
    // If a user has set a voice for this language, return that voice
    // Check that chosen voice is supported by current device
    const userChosenVoiceForLanguage = ttsSettings.chosenVoiceForLanguage[currentTTSLanguageForDoc];
    if (
      userChosenVoiceForLanguage &&
      TextToSpeechVoicesByLanguage[currentTTSLanguageForDoc].includes(userChosenVoiceForLanguage)
    ) {
      return userChosenVoiceForLanguage;
    }
    const defaultVoiceSettings = ttsSettings.defaultVoiceSettings;
    const defaultGender = defaultVoiceSettings.defaultGender;
    const defaultLanguage = defaultVoiceSettings.defaultLanguage;

    // Return either the default voice for language, or user default language and gender,
    // or if all else fails, the true default (female english)
    return (
      this._getDefaultVoiceForLanguageAndGender(currentTTSLanguageForDoc, defaultGender) ??
      // if there is no chosen voice, default to whichever voice is default for this language based on defaultVoiceSettings
      this._getDefaultVoiceForLanguageAndGender(defaultLanguage, defaultGender) ??
      // if not existent, select the voice that matches the default preferences for all TTS
      this._getDefaultVoiceForLanguageAndGender(textToSpeechDefaultLanguage, textToSpeechDefaultGender)
    );
  }

  /**
   * Get TTS word boundaries for a voice.
   * - Reader: reads from transient state
   * - Bookwise: reads from cache
   */
  async getTTSWordBoundariesForVoice(
    playingDocId: string,
    voice: TextToSpeechVoice,
  ): Promise<{[chunkId: string]: WordBoundary[];} | undefined> {
    if (!playingDocId || !voice) {
      return;
    }

    const apiVersion = TextToSpeechVoiceToApiVersion[voice];

    if (isBookwise) {
      const cachedData = await background.getTtsWordBoundaries(playingDocId, voice);
      if (!cachedData) {
        return;
      }
      return apiVersion === 'v3' ? cachedData.word_boundaries_v3_chunked : cachedData.word_boundaries_v2_chunked;
    }

    // Reader: read from transient state
    const state = globalState.getState();
    const boundaries = state.transientDocumentsData[playingDocId]?.tts;
    if (!boundaries || !boundaries[voice]) {
      return;
    }
    return apiVersion === 'v3' ? boundaries[voice].word_boundaries_v3_chunked : boundaries[voice].word_boundaries_v2_chunked;
  }

  isDocumentUsingUnrealSpeech(doc: FirstClassDocument): boolean {
    const voice = this.getVoiceForDocument(doc);
    return TextToSpeechVoiceToApiVersion[voice] === 'v3';
  }

  async increasePlaybackRatePreference(options: StateUpdateOptionsWithoutEventName): Promise<void> {
    const oldRate = this._getSettings()?.playbackRate;
    const possibleNewRate =
      typeof oldRate === 'undefined'
        ? textToSpeechDefaultPlaybackRate
        : PlaybackRates.find((rate) => rate > oldRate);
    if (typeof possibleNewRate !== 'number') {
      return;
    }
    return this.setPlaybackRatePreference(possibleNewRate, {
      ...options,
      eventName: 'settings-tts-playback-rate-increased',
    });
  }

  async jumpBackward(): Promise<void> {
    await this._jump(-15);
  }

  async jumpForward(): Promise<void> {
    await this._jump(15);
  }

  async modifyVolumePreference(
    action: 'decrease' | 'increase',
    options: StateUpdateOptionsWithoutEventName,
  ): Promise<void> {
    const oldVolume = this._getSettings()?.volume;
    let newVolume: number;
    if (typeof oldVolume === 'number') {
      let delta = 0.1;
      if (action === 'decrease') {
        delta *= -1;
      }
      newVolume = oldVolume + delta;
      if (newVolume < 0.05) {
        // Round down
        newVolume = 0;
      }
    } else {
      newVolume = textToSpeechDefaultVolume;
    }

    return this.setVolumePreference(newVolume, {
      ...options,
      eventName: `settings-tts-volume-${action}d`,
    });
  }

  async pause(): Promise<void> {
    this.toggleTTSLoading(false);
    await this._setIsPlaying(false);
  }

  async toggleTTSLoading(loading: boolean) {
    return updateState(
      (state) => {
        if (loading === state.ttsLoading) {
          throw new CancelStateUpdate();
        }
        state.ttsLoading = loading;
      }, { eventName: 'set-tts-loading', userInteraction: null },
    );
  }

  async play(): Promise<void> {
    await this._setIsPlaying(true);
  }

  async playDocument(docId: string, startingChunkId: string, startPositionInChunk?: number): Promise<void> {
    if (this._startingToPlay) {
      return;
    }
    this.toggleTTSLoading(true);
    this._startingToPlay = true;
    let playbackQueued = false;
    try {
      await this._ensureDefaultPreferences();

      const tracks = await this._buildTracks(docId, startingChunkId, startPositionInChunk);
      if (tracks === undefined) {
        return;
      }
      await this._playTracks(tracks, startingChunkId, startPositionInChunk, docId);
      playbackQueued = true;
    } finally {
      this._startingToPlay = false;
      if (!playbackQueued) {
        // Queueing failed or bailed — clear the spinner ourselves. On success the engage
        // watchdog clears it once audio is actually running (we return long before that).
        this.toggleTTSLoading(false);
      }
    }
  }

  async playOrPauseDocument(docId: string, chunkId: string) {
    const playingDocId = globalState.getState().tts?.playingDocId;
    if (docId !== playingDocId) {
      await this.playDocument(docId, chunkId);
      return;
    }
    switch (await this._getTrackPlayerState()) {
      case TrackPlayerState.Loading:
      case TrackPlayerState.Playing:
        await this.pause();
        break;
      case TrackPlayerState.Off:
        await this.playDocument(docId, chunkId);
        break;
      case TrackPlayerState.Paused:
        await this.play();
        break;
    }
  }

  async playOrPauseCurrentlyPlayingDocumentOrPlayNewDocument(
    chunkId?: string,
    newDocId?: FirstClassDocument['id'] | null,
  ): Promise<void> {
    if (globalState.getState().tts?.playingDocId) {
      await this.resumeOrPauseCurrentlyPlayingDocument();
    } else if (newDocId && chunkId) {
      await this.playDocument(newDocId, chunkId);
    }
  }

  async playOrStopDocument({
    docId,
    shouldPlayIfPaused,
    chunkId,
    startingPosition,
  }: {
    docId: string;
    shouldPlayIfPaused?: boolean;
    chunkId: string;
    startingPosition?: number;
  }) {
    const playingDocId = globalState.getState().tts?.playingDocId;
    if (docId !== playingDocId) {
      await this.playDocument(docId, chunkId, startingPosition);
      return;
    }
    switch (await this._getTrackPlayerState()) {
      case TrackPlayerState.Loading:
      case TrackPlayerState.Playing:
        await this.stop();
        break;
      case TrackPlayerState.Off:
        await this.playDocument(docId, chunkId, startingPosition);
        break;
      case TrackPlayerState.Paused:
        if (shouldPlayIfPaused) {
          await this.play();
        } else {
          await this.stop();
        }
        break;
    }
  }

  async resumeOrPauseCurrentlyPlayingDocument(): Promise<void> {
    const playingDocId = globalState.getState().tts?.playingDocId;
    if (!playingDocId) {
      throw new Error('There is no playing document');
    }
    const playingChunkId = globalState.getState().tts?.playingChunkId;
    if (!playingChunkId) {
      throw new Error('There is no playing chunk Id');
    }
    return this.playOrPauseDocument(playingDocId, playingChunkId);
  }

  async seek(position: number, chunkId?: string) {
    const playingChunkId = globalState.getState().tts?.playingChunkId;
    if (!playingChunkId && !chunkId) {
      throw new Error('Seek: There is no playing chunk Id');
    }
    // If we began playback mid-chunk, the starting chunk's track was built with that start
    // position (on mobile its URL carries startPos and the backend serves silence for the
    // bytes before it). Seeking back to the very start of that chunk would land in the
    // silence fill, so rebuild its track with the new start position instead. Keyed on
    // startingChunkId, not playingChunkId — auto-advance moves playingChunkId but the
    // start position only ever applied to the chunk playback began in.
    const ttsState = globalState.getState().tts;
    const startingPositionInChunk = ttsState?.startingPositionInChunk ?? 0;
    const targetChunkId = chunkId ?? playingChunkId;
    if (
      targetChunkId === ttsState?.startingChunkId &&
      startingPositionInChunk > SEEK_POSITION_TOLERANCE_SECONDS &&
      position <= SEEK_POSITION_TOLERANCE_SECONDS
    ) {
      // A bare seek (e.g. RemoteSeek scrubbing while paused) must preserve play/pause state;
      // only seekAndPlay resumes. _reloadTracks force-plays via _playTracks, so re-pause if
      // the user was paused. pause() also sets _pausedSincePlaybackStart, stopping the
      // reload's engage watchdog from re-issuing play.
      const wasPlaying = (await this._getRawTrackPlayerState()) === TrackPlayerState.Playing;
      await this._reloadTracks(Math.max(position, 0), targetChunkId);
      if (!wasPlaying) {
        await this.pause();
      }
      return;
    }
    if (chunkId && playingChunkId !== chunkId) {
      await this._skipToTrackByChunkId(chunkId);
    }
    let targetPosition = position;
    if (targetPosition < 0) {
      const currentTrackIndex = await this._getCurrentTrackIndex();
      if (currentTrackIndex > 0) {
        await this._skipToTrackByIndex(Math.max(currentTrackIndex - 1, 0));
        const trackLength = await this._getCurrentTrackLength();
        // Go back at least 5 seconds so we dont immediately return back to the next chunk
        // hopping chunks is expensive
        targetPosition = trackLength - Math.min(5, -targetPosition);
      }
    } else if (targetPosition > await this._getCurrentTrackLength()) {
      const currentTrackIndex = await this._getCurrentTrackIndex();
      const allTracks = await this._getAllActiveTracks();
      await this._skipToTrackByIndex(Math.min(currentTrackIndex + 1, allTracks.length - 1));
      targetPosition = 0;
    }
    await this.seekWithinChunkAndRetryUntilSuccessful(Math.max(targetPosition, 0));
  }

  async trySeekingToNextTrackIfAtTheEnd() {
    const duration = await this._getCurrentTrackLength();
    const progress = await this._getTrackPlayerPosition();
    // 1.3 derived from experiment but basically if we are close to the end of a track try playing the next one
    if (Math.abs(duration - progress) > 1.3) {
      return;
    }
    const tracks = await this._getAllActiveTracks();
    const currentIndex = await this._getCurrentTrackIndex();
    if (currentIndex + 1 > tracks.length - 1) {
      return;
    }
    await this._skipToTrackByIndex(currentIndex + 1);
    return this.play();
  }

  async setIsAutoScrollingEnabled(isEnabled: boolean) {
    await updateState(
      ({ tts }) => {
        if (!tts || tts.autoScrolling === isEnabled) {
          throw new CancelStateUpdate();
        }
        tts.autoScrolling = isEnabled;
      },
      {
        eventName: 'tts-auto-scrolling-set',
        userInteraction: null,
      },
    );
  }

  async setPlaybackRatePreference(
    rate: PlaybackRate,
    options: StateUpdateOptionsWithoutEventName & Partial<Pick<StateUpdateOptions, 'eventName'>>,
  ): Promise<void> {
    const prevRate = this._getSettings()?.playbackRate;
    if (rate === prevRate) {
      return;
    }
    await updateState(
      ({ persistent: { settings } }) => {
        if (!settings.tts_v2) {
          throw new Error('TtsController.setPlaybackRatePreference: tts preferences do not exist');
        }
        settings.tts_v2.playbackRate = rate;
      },
      {
        eventName: 'settings-tts-playback-rate-set',
        isUndoable: false,
        ...options,
      },
    );
  }

  async setVolumePreference(
    volume: number,
    options: StateUpdateOptionsWithoutEventName & Partial<Pick<StateUpdateOptions, 'eventName'>>,
  ): Promise<void> {
    const previousVolume = this._getSettings()?.volume;
    const fixedVolume = Math.max(0, Math.min(volume, 1));
    if (fixedVolume !== previousVolume) {
      await updateState(
        ({ persistent: { settings } }) => {
          if (!settings.tts_v2) {
            throw new Error('TtsController.setPlaybackRatePreference: tts preferences do not exist');
          }
          settings.tts_v2.volume = fixedVolume;
        },
        {
          eventName: 'settings-tts-volume-set',
          isUndoable: false,
          ...options,
        },
      );
    }

    // If it becomes muted, pause (to save money)
    if (!fixedVolume) {
      this.pause();
    }
  }

  async setVoicePreferenceAndLanguage({
    documentId,
    language,
    userInteraction,
    voice,
  }: {
    documentId: FirstClassDocument['id'];
    language: TTSLanguage;
    userInteraction: string | null;
    voice: TextToSpeechVoice;
  }) {
    await this._ensureDefaultPreferences();
    await updateState(
      (state) => {
        if (!state.persistent.settings.tts_v2) {
          throw new Error('no tts in persistentState???'); // should never happen
        }
        if (state.persistent.settings.tts_v2.chosenVoiceForLanguage[language] === voice) {
          throw new CancelStateUpdate();
        }
        state.persistent.settings.tts_v2.chosenVoiceForLanguage[language] = voice;
      },
      {
        eventName: 'tts-voice-updated-for-language',
        userInteraction: null,
      },
    );

    const currentTTSLanguage = await this.getCurrentTTSLanguageForDocId(documentId);

    if (language !== currentTTSLanguage) {
      await overrideLanguage(documentId, { language }, { userInteraction });
    } else {
      createToast({
        content: 'Voice updated',
        category: 'success',
      });
    }
    await this._reloadTracks();
  }

  async setTrackPlayerInfo(info: Partial<TextToSpeechInfo['trackPlayerInfo']>) {
    if (!globalState.getState().tts) {
      return;
    }

    await updateState(
      (state) => {
        if (!state.tts) {
          throw new Error('!state.tts');
        }
        state.tts.trackPlayerInfo ??= { isPlaying: false, position: 0, ...info };
        state.tts.trackPlayerInfo = { ...state.tts.trackPlayerInfo, ...info };
      },
      {
        eventName: 'tts-track-player-info-set',
        isUndoable: false,
        userInteraction: null,
      },
    );
  }

  async stop(): Promise<void> {
    await this._resetTrackPlayer();
    await updateState(
      (state) => {
        if (!state.tts) {
          throw new CancelStateUpdate();
        }
        state.tts = null;
        state.ttsFetchingTimestamp = false;
        state.ttsLoading = false;
      },
      {
        eventName: 'tts-stopped',
        userInteraction: null,
      },
    );
  }

  async switchVoiceToV2(docParam: FirstClassDocument | null, userInteraction: string | null) {
    const docId = globalState.getState().tts?.playingDocId;
    const doc = docParam ?? await database.collections.documents.findOne<FirstClassDocument>(docId);
    if (!doc) {
      return;
    }
    const voice = this.getVoiceForDocument(doc);
    if (TextToSpeechVoiceToApiVersion[voice] === 'v2') {
      return;
    }
    const gender = TextToSpeechGenderByVoice[voice];
    const newVoice = gender === 'male' ? TextToSpeechVoice.Guy : TextToSpeechVoice.Aria;
    await this.setVoicePreferenceAndLanguage({
      documentId: doc.id,
      language: textToSpeechDefaultLanguage,
      voice: newVoice,
      userInteraction,
    });
    createToast({
      content: 'Voice updated',
      category: 'success',
    });
  }

  async switchVoiceToV3(doc: FirstClassDocument) {
    if (!doc) {
      return;
    }
    const voice = this.getVoiceForDocument(doc);
    if (TextToSpeechVoiceToApiVersion[voice] === 'v3') {
      return;
    }
    const gender = TextToSpeechGenderByVoice[voice];
    const newVoice = this._getDefaultVoiceForLanguageAndGender(textToSpeechDefaultLanguage, gender);
    await this.setVoicePreferenceAndLanguage({
      documentId: doc.id,
      language: textToSpeechDefaultLanguage,
      voice: newVoice,
      userInteraction: 'todo', // TODO
    });
    createToast({
      content: 'Voice updated',
      category: 'success',
    });
  }

  async toggleIsPlaying(): Promise<boolean> {
    return this._setIsPlaying(!await this._isTrackPlayerPlaying());
  }

  /*
    TrackPlayer (on react-native at least) does not always successfully seekTo a position we request, (perhaps it tries
    its best and fails).
    This helps us ensure that after a certain delay we are at the right position
  */
  async seekWithinChunkAndRetryUntilSuccessful(targetPosition: number, numberOfRetries = 0) {
    if (numberOfRetries > 500) {
      return;
    }
    await this._seekTrackPlayerWithinChunk(targetPosition);
    const newPosition = await this._getTrackPlayerPosition();
    if (Math.abs(newPosition - targetPosition) < SEEK_POSITION_TOLERANCE_SECONDS) {
      return;
    }
    await delay(20);
    await this.seekWithinChunkAndRetryUntilSuccessful(targetPosition, numberOfRetries + 1);
  }

  abstract updateCurrentPlayingTrackInState(): Promise<void>;

  async getWebviewDocumentChunksForCurrentPlayingDocument(): Promise<WebviewDocumentChunk[] | null> {
    const docId = globalState.getState().tts?.playingDocId;
    if (!docId) {
      return null;
    }

    return getAllWebviewChunksForDocId(docId);
  }

  /*
    Seek (possibly across chunks) and resume playback, keeping the loading state up until
    audio at the target is actually running — a cold target needs a full synthesis round
    trip even though the seek itself returns quickly.
  */
  async seekAndPlay(position: number, chunkId?: string) {
    this.toggleTTSLoading(true);
    try {
      await this.seek(position, chunkId);
      this._pausedSincePlaybackStart = false;
      await this.play();
    } catch (error) {
      this.toggleTTSLoading(false);
      throw error;
    }
    this._ensurePlaybackEngages(position);
  }

  abstract _skipToTrackByChunkId(chunkId: string): Promise<void>;
  abstract _getCurrentTrackIndex(): Promise<number>;
  abstract _skipToTrackByIndex(index: number): Promise<void>;
  abstract _getCurrentTrackLength(): Promise<number>;

  abstract _setQueue(tracks: TTrack[]): Promise<void>;
  abstract _getAllActiveTracks(): Promise<TTrack[]>;


  async _buildTracks(
    docIdArgument: string | undefined,
    startingChunkId?: string,
    startPositionInChunk?: number,
  ): Promise<TTrack[] | undefined> {
    const docId = docIdArgument ?? globalState.getState().tts?.playingDocId;
    if (!docId) {
      throw new Error('_buildTrack: need a docId');
    }
    const doc = await database.collections.documents.findOne<FirstClassDocument>(docId);
    const allChunkIds = getSpineForDoc(docId);
    if (!doc) {
      throw new Error(`_buildTrack: can't find document with id ${docId}`);
    }


    const currentVoice = this.getVoiceForDocument(doc);

    const tracks: TTrack[] = [];
    for (const chunkId of allChunkIds) {
      // Pass the start position only for the chunk we begin playback in, so the backend
      // can serve cheap silence for the player's initial bytes=0 request instead of
      // synthesizing audio we will immediately seek past.
      const startPositionForChunk =
        startingChunkId === chunkId && startPositionInChunk ? startPositionInChunk : undefined;
      const chunkUrl = this._buildTrackUrl(doc, currentVoice, chunkId, startPositionForChunk);
      if (!chunkUrl) {
        return undefined;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      tracks.push({ // Explanation: TTrack is a subtype of TextToSpeechTrack; the object literal satisfies all required fields and the cast is needed for the generic type parameter
        artist: doc.author || '',
        artwork: getDocumentOverrideOrReal(doc, 'image_url') ?? 'placeholderUrl',
        title: doc.title,
        url: chunkUrl,
        docId,
        chunkId,
      } as TTrack);
    }
    return tracks;
  }

  _buildTrackUrl(
    doc: FirstClassDocument,
    voice: TextToSpeechVoice,
    chunkId: string,
    // Used by platform overrides to signal the intended start position to the backend
    startPosition?: number,
  ): string | undefined {
    const versionEndpoint = TextToSpeechVoiceToApiVersion[voice];
    return `${getServerBaseUrl()}/reader/api/documents/${
      doc.id
    }/tts_stream_${versionEndpoint}.mp3?voice=${voice}&chunkId=${chunkId}`;
  }

  _convertLanguageToTTSLanguage(
    languageCode: DocumentWithLanguage['language'],
  ): TTSLanguage | undefined {
    if (!languageCode) {
      return undefined;
    }
    const convertedLanguageCode = languageCode.split('-')[0];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    if (TextToSpeechVoicesByLanguage[convertedLanguageCode as TTSLanguage]) { // Explanation: checking if the language code is a valid TTSLanguage key; the cast is safe because invalid codes simply return undefined
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return convertedLanguageCode as TTSLanguage; // Explanation: verified above that convertedLanguageCode is a valid TTSLanguage key
    }
    return undefined;
  }

  async _enableAutoScrollingIfSettingIsEnabled() {
    if (globalState.getState().tts?.autoScrolling) {
      return this.setIsAutoScrollingEnabled(true);
    }
  }

  async _ensureDefaultPreferences(): Promise<void> {
    if (this._getSettings()) {
      return;
    }
    await updateState(
      ({ persistent: { settings } }) => {
        settings.tts_v2 = {
          chosenVoiceForLanguage: {},
          defaultVoiceSettings: {
            defaultGender: textToSpeechDefaultGender,
            defaultLanguage: textToSpeechDefaultLanguage,
          },
          playbackRate: textToSpeechDefaultPlaybackRate,
        };
      },
      { userInteraction: null, eventName: 'tts-default-settings-set', isUndoable: false },
    );
  }

  _getDefaultVoiceForLanguageAndGender(
    language: TTSLanguage,
    gender: TextToSpeechGender,
  ): TextToSpeechVoice {
    const allVoicesForLanguage = TextToSpeechVoicesByLanguage[language];
    return allVoicesForLanguage.filter((voice) => TextToSpeechGenderByVoice[voice] === gender)[0];
  }

  _getSettings(): TextToSpeechSettings | undefined {
    return globalState.getState().persistent.settings.tts_v2;
  }

  abstract _getTrackPlayerPosition(): Promise<number>;
  abstract _getTrackPlayerState(): Promise<TrackPlayerState>;

  async _isTrackPlayerPlaying(): Promise<boolean> {
    return await this._getTrackPlayerState() === TrackPlayerState.Playing;
  }

  async _jump(seconds: number) {
    const position = await Promise.resolve(this._getTrackPlayerPosition());
    const duration = await this._getCurrentTrackLength();

    // If the jump goes the end of doc,we want to allow half a second of playtime
    // for the word boundary to update.
    await this.seek(Math.min(position + seconds, duration));
    await this._enableAutoScrollingIfSettingIsEnabled();
    await this.play();
  }

  async _onLanguageUpdated(documentId: FirstClassDocument['id'], language: TTSLanguage) {
    // Do nothing
  }

  abstract _pauseTrackPlayer(): Promise<void>;

  async _playTracks(tracks: TTrack[], startingChunkId: string, startPositionInChunk: number | undefined, docId?: string) {
    this._pausedSincePlaybackStart = false;
    await this._resetTrackPlayer();
    await this._setQueue(tracks);
    await this._skipToTrackByChunkId(startingChunkId);

    await updateState(
      (state) => {
        const playingDocId = docId ?? state.tts?.playingDocId;
        if (!playingDocId) {
          exceptionHandler.captureException(new Error('TtsControl: playingDocId is undefined'), {
            extra: {
              tts: state.tts,
              ttsSettings: state.persistent.settings.tts_v2,
              tracks,
              startPosition: startPositionInChunk,
            },
          });
          return;
        }
        state.ttsFetchingTimestamp = false;
        state.tts = {
          autoScrolling: state.tts?.autoScrolling ?? true,
          startingChunkId,
          startingPositionInChunk: startPositionInChunk ?? 0,
          playingDocId,
          playingChunkId: startingChunkId,
          currentlyPlayingListQuery: state.focusedDocumentListQuery,
        };
      },
      {
        eventName: 'tts-document-playback-started',
        userInteraction: null,
      },
    );

    if (startPositionInChunk) {
      // Start playback before seeking: the track player restores the pre-seek play state
      // when a pending seek completes, so seeking first would snap back to paused. Any
      // audio before the seek lands is the backend's silence fill, so nothing is audible.
      await this.play();
      await this.seekWithinChunkAndRetryUntilSuccessful(startPositionInChunk);
    }
    await this.play();
    this._ensurePlaybackEngages(startPositionInChunk ?? 0);
  }

  /*
    Watchdog for the first moments of playback: while the player is still buffering the
    seek target, a completing seek can flip it back to paused on its own. Re-issue play
    until playback actually engages — but never fight a pause that came from somewhere
    else (any controller pause path sets _pausedSincePlaybackStart, and a pause that
    survives a re-issued play means a native source like an audio interruption owns it).

    Also owns clearing the loading spinner: playDocument returns long before audio is
    buffered at the start position, so the loading state stays on until playback runs
    (or this watchdog gives up).
  */
  async _ensurePlaybackEngages(startPositionInChunk: number) {
    // Newer playback/seek operations supersede this watchdog: each spawns its own, and
    // only the latest may clear the spinner or re-issue play.
    const generation = ++this._playbackEngageGeneration;
    try {
      let consecutivePausedObservations = 0;
      for (let i = 0; i < 40; i++) {
        await delay(300);
        if (
          generation !== this._playbackEngageGeneration ||
          !globalState.getState().tts ||
          this._pausedSincePlaybackStart
        ) {
          return;
        }
        // Raw player state: on mobile, _getTrackPlayerState mixes ttsLoading into Loading —
        // exactly the state this watchdog runs under (web's is already raw).
        const state = await this._getRawTrackPlayerState();
        if (state === TrackPlayerState.Playing) {
          // The player reports Playing while running through the silence-filled lead-in:
          // its seek snaps to the nearest buffered position, which sits just before the
          // start position until real audio is synthesized. Only call playback engaged
          // once the playhead has moved past where the speech actually starts.
          const position = await this._getTrackPlayerPosition();
          if (position > startPositionInChunk + 0.3) {
            return;
          }
          consecutivePausedObservations = 0;
          continue;
        }
        if (state === TrackPlayerState.Paused) {
          consecutivePausedObservations += 1;
          if (consecutivePausedObservations >= 2) {
            return;
          }
          await this._startPlayingTrackPlayer();
        } else {
          consecutivePausedObservations = 0;
        }
      }
    } catch (error) {
      // Fire-and-forget callers don't await this; report instead of rejecting unhandled.
      exceptionHandler.captureException(error, { extra: { startPositionInChunk } });
    } finally {
      if (generation === this._playbackEngageGeneration) {
        this.toggleTTSLoading(false);
      }
    }
  }

  /*
    The native player's own state, without the ttsLoading/ttsFetchingTimestamp overlays
    that _getTrackPlayerState mixes in for UI purposes. Platforms whose
    _getTrackPlayerState is already raw can use this default.
  */
  async _getRawTrackPlayerState(): Promise<TrackPlayerState> {
    return this._getTrackPlayerState();
  }

  async _reloadTracks(newPosition?: number, targetChunkId?: string) {
    if (!globalState.getState().tts?.playingDocId) {
      return;
    }
    const position = newPosition ?? await this._getTrackPlayerPosition();
    const chunkId = targetChunkId ?? globalState.getState().tts?.playingChunkId;
    const tracks = await this._buildTracks(undefined, chunkId, position);
    if (!tracks || !chunkId) {
      await this.stop();
      return;
    }
    await this._playTracks(tracks, chunkId, position);
  }

  abstract _resetTrackPlayer(): Promise<void>;

  abstract _seekTrackPlayerWithinChunk(position: number): Promise<void>;

  async _setIsPlaying(isPlaying: boolean): Promise<boolean> {
    const state = await this._getTrackPlayerState();
    if (isPlaying) {
      // If it's muted, turn it up
      if (this._getSettings()?.volume === 0) {
        await this.setVolumePreference(0.3, { userInteraction: null });
      }

      if (state !== TrackPlayerState.Playing) {
        await this._startPlayingTrackPlayer();
        await this._enableAutoScrollingIfSettingIsEnabled();
      }
      return true;
    } else {
      // Set here rather than in pause() so every pause path (toggleIsPlaying, the
      // player bar, remote events routed through the controller) stops the startup
      // watchdog in _ensurePlaybackEngages.
      this._pausedSincePlaybackStart = true;
      if (state !== TrackPlayerState.Paused) {
        await this._pauseTrackPlayer();
      }
      return false;
    }
  }

  abstract _startPlayingTrackPlayer(): Promise<void>;
}

export default AbstractTtsController;
